import type { PostureAnalysis } from '@/lib/types';
import type { GhlCalendarEvent } from '@/lib/prospects-model';
import logger from '@/lib/logging';
import type { ProspectContact } from './prospect-contact';
import { buildProspectPosturePayload } from './prospect-payload';

async function parseError(res: Response): Promise<string> {
  const data = await res.json().catch(() => ({}));
  if ((data as { error?: string })?.error) {
    return (data as { error: string }).error;
  }
  if (res.status === 413) {
    return 'Request payload is too large. Please try again.';
  }
  return `Server error (${res.status})`;
}

/** Create a client prospect when the user supplies name + email for PDF download. */
export async function createClientProspect(
  contact: ProspectContact,
  gdprConsent: boolean,
  pictureConsent = false,
): Promise<string> {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const deviceType = isMobile ? 'mobile' : 'desktop';
  const timezone =
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
  const pageUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';

  const res = await fetch('/client-prospects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: contact.name,
      email: contact.email,
      gdprConsent,
      pictureConsent: pictureConsent === true,
      deviceType,
      userAgent: ua.slice(0, 300),
      ...(contact.ghlSurvey ? { ghlSurvey: contact.ghlSurvey } : {}),
      timezone,
      pageUrl,
    }),
  });
  if (!res.ok) {
    throw new Error(await parseError(res));
  }
  const data = (await res.json()) as { id: string };
  if (!data.id) {
    throw new Error('Invalid server response.');
  }
  return data.id;
}

/** Attach posture analysis to an existing prospect (after optional save step). */
export async function linkProspectPostureAnalysis(
  prospectId: string,
  analysis: PostureAnalysis,
  gdprConsent: boolean,
  savePictures: boolean = true,
): Promise<void> {
  const posturePayload = await buildProspectPosturePayload(analysis, gdprConsent, savePictures);
  const res = await fetch(`/client-prospects/${encodeURIComponent(prospectId)}/posture-analyses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      gdprConsent,
      savePictures,
      postureAnalysis: posturePayload,
    }),
  });
  if (!res.ok) {
    // Already linked for this prospect — id is enough for sales URL prefill.
    if (res.status === 409) return;
    throw new Error(await parseError(res));
  }
}

/** Persist GHL calendar open / booking. Missing prospect is non-fatal. */
export async function recordProspectGhlCalendarEvent(
  prospectId: string,
  event: GhlCalendarEvent,
): Promise<void> {
  const res = await fetch(`/client-prospects/${encodeURIComponent(prospectId)}/ghl-calendar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event }),
  });
  if (res.status === 404) return;
  if (!res.ok) {
    throw new Error(await parseError(res));
  }
}

/** Fire-and-forget stamp used by the self-assessment booking widget. */
export async function persistProspectGhlCalendarEvent(
  prospectId: string | null | undefined,
  event: GhlCalendarEvent,
): Promise<void> {
  if (!prospectId) return;
  try {
    await recordProspectGhlCalendarEvent(prospectId, event);
  } catch (err) {
    logger.error('[ClientProspects] Failed to record GHL calendar event', {
      prospectId,
      event,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
